import { FC } from 'react';
import {
  DesignVariantProvider,
  useDesignVariant,
} from '@shared/designVariants/DesignVariantProvider';
import { DesignHeader } from '@shared/designVariants/DesignHeader';
import {
  DesignWorkspaceProvider,
  useDesignWorkspace,
} from '@shared/designVariants/DesignWorkspaceProvider';
import { WorkspaceDialog } from '@features/Workspace/WorkspaceDialog';
import { AskConversationProvider } from '@features/Workspace/AskConversationProvider';
import { GuidedCreationReceiptProvider } from '@features/Workspace/GuidedCreationReceiptProvider';
import { GlobalAskDock } from '@features/Workspace/GlobalAskDock';
import { DesignSidebar } from '@shared/designVariants/DesignSidebar';
import { styled, Container, Box, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Outlet } from 'react-router-dom';
import Header from './vertical/header/Header';
import SidebarV2 from './vertical/sidebar/SidebarV2';
import { RootState, useAppSelector } from '@shared/store';
import { StartStopProvider } from '@features/Admin/Campaign/contexts/startstop.provider';

const MainWrapper = styled('div')(() => ({
  display: 'flex',
  minHeight: '100vh',
  width: '100%',
}));

const PageWrapper = styled('div')(() => ({
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'column',
  zIndex: 1,
  width: '100%',
  minWidth: 0,
  backgroundColor: 'transparent',
  overflow: 'auto',
  scrollbarGutter: 'stable',
  height: '100vh',
}));

const WorkspaceLayout: FC<{ enableDesignVariants: boolean }> = ({ enableDesignVariants }) => {
  const { variant } = useDesignVariant();
  const workspace = useDesignWorkspace();
  const alternate = variant !== 'main';
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const customizer = useAppSelector((state: RootState) => state.customizer);
  return (
    <StartStopProvider>
      <MainWrapper
        sx={
          alternate
            ? (theme) => ({
                bgcolor: 'background.default',
                color: 'text.primary',
                fontFamily: theme.typography.fontFamily,
              })
            : undefined
        }>
        {variant === 'main' ? <SidebarV2 /> : <DesignSidebar variant={variant} />}
        <PageWrapper
          className="page-wrapper"
          data-design-variant={variant}
          sx={{
            ...(['v4', 'v5'].includes(variant) && {
              my: { xs: 0, md: variant === 'v5' ? 1.5 : 1 },
              mr: { xs: 0, md: variant === 'v5' ? 1.5 : 1 },
              border: 1,
              borderColor: 'divider',
              borderRadius: { xs: 0, md: variant === 'v5' ? '18px' : '16px' },
              height: {
                xs: '100dvh',
                md: variant === 'v5' ? 'calc(100dvh - 24px)' : 'calc(100dvh - 16px)',
              },
              bgcolor: 'background.paper',
            }),
            ...(lgUp &&
              !alternate &&
              customizer.isCollapse && { ml: `${customizer.MiniSidebarWidth}px` }),
          }}>
          {alternate ? <DesignHeader /> : <Header showDesignChooser={enableDesignVariants} />}
          <Box
            sx={{
              display: alternate ? 'flex' : 'contents',
              flex: alternate ? 1 : undefined,
              minHeight: 0,
              alignItems: 'stretch',
            }}>
            <Container
              sx={{
                height: '100%',
                ...(alternate && {
                  flex: 1,
                  minWidth: 0,
                  overflowY: 'auto',
                  bgcolor: ['v4', 'v5'].includes(variant)
                    ? 'background.paper'
                    : 'background.default',
                  py: { xs: 3, md: variant === 'v5' ? 4.5 : 3.5 },
                  px: { xs: 2, md: variant === 'v5' ? 5 : 3.5 },
                }),
                maxWidth: alternate
                  ? '100%!important'
                  : customizer.isLayout === 'boxed'
                    ? 'lg'
                    : '100%!important',
              }}>
              <Box
                sx={{
                  minHeight: 'calc(100vh - 170px)',
                  height: '100%',
                  position: 'relative',
                  '> div': { height: '100%' },
                }}>
                <Box
                  hidden={alternate && !!workspace.module}
                  sx={{ display: alternate && workspace.module ? 'none' : undefined }}>
                  <Outlet />
                </Box>
                {alternate && workspace.module && (
                  <WorkspaceDialog
                    key={workspace.module}
                    module={workspace.module}
                    onClose={workspace.close}
                    embedded
                  />
                )}
              </Box>
            </Container>
            {alternate && <GlobalAskDock />}
          </Box>
        </PageWrapper>
      </MainWrapper>
    </StartStopProvider>
  );
};

export const FullLayout: FC<{ enableDesignVariants?: boolean }> = ({
  enableDesignVariants = false,
}) => (
  <DesignVariantProvider enabled={enableDesignVariants}>
    <DesignWorkspaceProvider>
      <AskConversationProvider>
        <GuidedCreationReceiptProvider>
          <WorkspaceLayout enableDesignVariants={enableDesignVariants} />
        </GuidedCreationReceiptProvider>
      </AskConversationProvider>
    </DesignWorkspaceProvider>
  </DesignVariantProvider>
);
